import { useTranslation } from "react-i18next";

export default function ArticleContent() {
  const { t } = useTranslation("blog-isde");

  const voorwaardenItems = t("sections.voorwaarden.items", { returnObjects: true }) as Array<{
    title: string;
    text: string;
  }>;

  const aanvraagSteps = t("sections.hoeAanvragen.steps", { returnObjects: true }) as Array<{
    num: string;
    title: string;
    text: string;
  }>;

  const bedragenRows = t("sections.bedragen.tableRows", { returnObjects: true }) as string[][];

  return (
    <div className="flex flex-col gap-10 md:gap-14">
      {/* Wat is ISDE */}
      <section id="wat-is-isde">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.watIs.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.watIs.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.watIs.p2")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed">
          {t("sections.watIs.p3")}
        </p>
      </section>

      {/* Bedragen */}
      <section id="bedragen">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.bedragen.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.bedragen.intro")}
        </p>
        <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
          <table className="w-full text-left border-collapse min-w-[560px]">
            <thead>
              <tr className="border-b-2 border-brand-border">
                {(
                  t("sections.bedragen.tableHead", { returnObjects: true }) as string[]
                ).map((head, i) => (
                  <th
                    key={i}
                    className="font-geist font-semibold text-sm text-brand-text py-3 pr-4"
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bedragenRows.map((row, ri) => (
                <tr key={ri} className="border-b border-brand-border/40">
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={`font-geist text-sm py-3 pr-4 ${
                        ci === 0
                          ? "text-brand-text font-medium"
                          : "text-brand-text/70"
                      }`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mt-5 md:mt-6">
          {t("sections.bedragen.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mt-3">
          {t("sections.bedragen.p2")}
        </p>
      </section>

      {/* Voorwaarden */}
      <section id="voorwaarden">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.voorwaarden.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.voorwaarden.intro")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {voorwaardenItems.map((item, idx) => (
            <div
              key={idx}
              className="bg-brand-cream rounded-xl p-4 md:p-5 border border-brand-border"
            >
              <h3 className="font-geist font-semibold text-sm md:text-base text-brand-text mb-1.5 md:mb-2">
                {item.title}
              </h3>
              <p className="font-geist text-sm text-brand-text/70 leading-relaxed">
                {item.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Hoe aanvragen */}
      <section id="hoe-aanvragen">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.hoeAanvragen.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.hoeAanvragen.intro")}
        </p>
        <div className="flex flex-col gap-4 md:gap-5">
          {aanvraagSteps.map((step, idx) => (
            <div key={idx} className="flex gap-3 md:gap-4">
              <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-brand-yellow flex items-center justify-center">
                <span className="font-geist-mono text-sm md:text-base font-bold text-brand-text">
                  {step.num}
                </span>
              </div>
              <div className="pt-0.5 md:pt-1">
                <h3 className="font-geist font-semibold text-sm md:text-base text-brand-text mb-1">
                  {step.title}
                </h3>
                <p className="font-geist text-sm text-brand-text/70 leading-relaxed">
                  {step.text}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 md:mt-6 bg-[#FFF9E0] border border-[#FFD600] rounded-xl p-4 md:p-5 flex gap-3 items-start">
          <i className="ri-lightbulb-line w-5 h-5 flex items-center justify-center text-brand-text mt-0.5 text-lg"></i>
          <p className="font-geist text-sm text-brand-text/80 leading-relaxed">
            {t("sections.hoeAanvragen.tip")}
          </p>
        </div>
      </section>

      {/* Combineren */}
      <section id="combineren">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.combineren.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.combineren.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.combineren.p2")}
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            href={t("sections.combineren.linkGlas.href")}
            className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text bg-brand-yellow px-4 py-2.5 rounded-full hover:bg-brand-yellow/90 transition-colors whitespace-nowrap"
          >
            {t("sections.combineren.linkGlas.text")}
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
          </a>
          <a
            href={t("sections.combineren.linkKozijn.href")}
            className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text border border-brand-border px-4 py-2.5 rounded-full hover:bg-brand-cream transition-colors whitespace-nowrap"
          >
            {t("sections.combineren.linkKozijn.text")}
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
          </a>
        </div>
      </section>

      {/* Wij helpen */}
      <section
        id="wij-helpen"
        className="bg-brand-text rounded-2xl p-6 md:p-10 text-white"
      >
        <h2 className="font-geist font-bold text-xl md:text-2xl text-white mb-3 md:mb-4">
          {t("sections.wijHelpen.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-white/80 leading-relaxed mb-3">
          {t("sections.wijHelpen.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-white/80 leading-relaxed mb-5 md:mb-6">
          {t("sections.wijHelpen.p2")}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href="/contact/"
            className="inline-flex items-center justify-center gap-1.5 font-geist font-bold text-sm bg-brand-yellow text-brand-text px-6 py-3 rounded-full hover:bg-brand-yellow/90 transition-colors whitespace-nowrap"
          >
            {t("sections.wijHelpen.cta")}
          </a>
          <a
            href="tel:+31407987470"
            className="inline-flex items-center justify-center gap-1.5 font-geist font-bold text-sm bg-white text-brand-text px-6 py-3 rounded-full hover:bg-white/90 transition-colors whitespace-nowrap"
          >
            <i className="ri-phone-line w-4 h-4 flex items-center justify-center text-sm"></i>
            {t("sections.wijHelpen.phone")}
          </a>
        </div>
      </section>
    </div>
  );
}